import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import { getSpaceId } from '../../utils/geo-space-id-helpers';
import { primaryButtonStyles } from '../../ui-components/components/component-styles/button';
import { colors, media } from '../../ui-components';
import LoadSpinner from '../load-spinner'; // Import LoadSpinner

const propsTypes = {
  portalId: PropTypes.string,
  loading: PropTypes.object,
  onSubmit: PropTypes.func,
  onReady: PropTypes.func,
  options: PropTypes.object,
  geoSpace: PropTypes.string,
  slug: PropTypes.string,
  isContactForm: PropTypes.bool
};

/**
 * Renders a HubSpot form.
 * For form config options, see https://developers.hubspot.com/docs/methods/forms/advanced_form_options
 *
 * Original source code: https://github.com/escaladesports/react-hubspot-form
 */
const HubSpotForm = props => {
  const {
    portalId = process.env.HUBSPOT_ACCOUNT_ID,
    loading,
    onSubmit,
    onReady,
    options,
    geoSpace,
    slug,
    hiddenFieldText,
    isGatedContentForm = false
  } = props;

  const [loaded, setLoaded] = useState(false);
  const [isFormPresent, setFormPresent] = useState(false);

  const { formMap } = useStaticQuery(graphql`
    query FormMapQuery {
      formMap: allContentfulFormMap {
        nodes {
          ...CompleteFormMap
        }
      }
    }
  `);

  function getFormId() {
    const spaceId = getSpaceId(geoSpace);

    const defaultFormId = isGatedContentForm
      ? process.env.HUBSPOT_DEFAULT_GATED_CONTENT_FORM_ID
      : process.env.HUBSPOT_DEFAULT_GLOBAL_FORM_ID;

    // Searches for a match in the locale space and the global space, will use a locale match over a
    // global match. If no matches, will return default form ID
    const localeResult = formMap?.nodes?.find(form => slug == form?.slug && spaceId == form?.spaceId);
    const globalResult = formMap?.nodes?.find(form => slug == form?.slug && getSpaceId('global') == form?.spaceId);
    const result = localeResult || globalResult;

    return result ? result['formId'] : defaultFormId;
  }

  const formId = getFormId();

  const el = useRef(null);

  const findFormElement = () => {
    // protect against component unmounting before form is added
    if (!el.current) {
      return;
    }

    let form = el.current.querySelector(`iframe`);

    if (form) {
      setLoaded(true);
      setFormPresent(true);
      if (onReady) {
        onReady(form);
      }
    } else {
      setTimeout(findFormElement, 1);
    }
  };

  const createForm = () => {
    if (window.hbspt) {
      // protect against component unmounting before window.hbspt is available
      if (!el.current) {
        return;
      }

      setTimeout(() => {
        // Add a 500ms timeout
        let formOptions = {
          ...options,
          portalId,
          formId,
          target: `#${el.current.getAttribute(`id`)}`,
          cssClass: `hubspot-form`,
          css: formCSS,
          onFormSubmitted: function () {
            onSubmit();
            el.current.reset();
          }
        };
        window.hbspt.forms.create(formOptions);
        findFormElement();
      }, 500); // Timeout duration
    } else {
      setTimeout(createForm, 1);
    }
  };

  const loadHubSpotScript = () => {
    if (!window.hbspt) {
      const script = document.createElement('script');
      script.src = 'https://js.hsforms.net/forms/v2.js';
      script.async = true;
      script.onload = createForm;
      document.body.appendChild(script);
    } else {
      createForm();
    }
  };

  const injectHiddenFieldText = () => {
    const textArea = document.querySelector(`#ai_maturity_assessment_results-${formId}`);
    if (textArea && hiddenFieldText) {
      textArea.value = hiddenFieldText;
    }
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      injectHiddenFieldText();
    }, 1000);

    // Cleanup function to clear the interval if the component unmounts
    return () => clearInterval(intervalId);
  }, [hiddenFieldText]);

  useEffect(() => {
    loadHubSpotScript();
  }, []);

  return (
    <div>
      {!isFormPresent && <LoadSpinner size={50} />} {/* Show spinner if form is not present */}
      <div ref={el} id={'hubspot-form-instance'} style={{ display: loaded ? 'block' : 'none' }} />
      {!loaded && loading}
    </div>
  );
};

/* the css outside of the .hubspot-form class pertains more to the safari browser since it responds differenlty */
const formCSS = `
  .hubspot-form {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 28px;
    align-self: stretch; 

    @media screen and (max-width: ${media.mediumTop}) {
      gap: 16px;
    }

    span {
      font-size: 14px;
      font-style: normal;
      font-weight: 700;
      line-height: 24px;
    }

    label { 
      color: ${colors.deepSmoke};
      display: flex;
      align-items: center;
    }

    select {
      background-color: ${colors.pureWhite};
      border: 1px solid ${colors.gray3};
      width: 100%;
      height: 52px;
      font-family: Lato, sans-serif;
      padding-left: 15px;
      padding-right: 15px;
    }

    fieldset {
      display: flex;
      gap: 28px;

      div {
        margin-right: 0px !important;
        width: 100% !important;
      }

      @media screen and (max-width: ${media.mediumTop}) {
        flex-direction: column;
        gap: 16px;
      }
    }

    textarea {
      height: 140px;
      border: 1px solid ${colors.gray3};
    }

    li {
      margin-bottom: 0px;
    }

    ul {
      list-style: none;
      
      label {
        font-size: 14px;
        color: red;

        span {
          color: ${colors.midnightBlack};
        }
      }
    }  
  }

  fieldset{
    width: 100%;
  }

  .hs-input[type="text"], .hs-input[type="email"], .hs-input[type="tel"], .is-placeholder{
    border: 1px solid ${colors.gray3};
    width: 100%;
    height: 52px;
    font-family: Lato, sans-serif;
  }

  .hs-fieldtype-textarea {
    border: 1px solid ${colors.gray3};
    font-family: Lato, sans-serif;
  }

  .hs-form-field {
    border: none;
  }

  .hs-form-booleancheckbox-display {
    display: flex;
    align-items: center;
    color: ${colors.midnightBlack};
  }

  .hs-form-checkbox-display {
    span {
      margin-left: 10px;
    }
  }

  .hs-error-msg, .hs-main-font-element {
    color: red;
  }
  
  ul {
    margin-left: 0;
  }

  .is-placeholder {
    color: ${colors.gray2};
  }

  .hs-button {
    ${primaryButtonStyles}
  }
`

HubSpotForm.propTypes = propsTypes;

export default HubSpotForm;
